import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";
import api from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";

export const MusicSection = () => {
  const [tracks, setTracks] = useState([]);
  const { playTrack, currentTrack, isPlaying } = usePlayer();

  useEffect(() => {
    api.get("/tracks").then((r) => setTracks(r.data)).catch(() => {});
  }, []);

  return (
    <section id="muziek" data-testid="music-section" className="py-28 sm:py-36">
      <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <div className="lg:sticky lg:top-32 self-start">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8 }}
            className="font-sans text-xs font-light tracking-[0.35em] uppercase text-terracotta-500 mb-6"
          >
            01 — Muziek
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-sand-900 leading-tight"
          >
            Liederen,
            <br />
            <em className="text-terracotta-500">bloot &amp; nabij</em>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-8 max-w-sm font-sans text-base font-light leading-relaxed text-sand-900/70"
          >
            Voorlopig kun je hier opnames horen die ik zelf opneem, thuis.
            Momenteel werk ik aan een nieuw album, <em className="font-serif">Beeldspraak</em> geheten.
            Alle stukken die ik hier presenteer, speel ik regelmatig voor mensen.
            Op die manier ontstaan er nog aanpassingen, die de uiteindelijke
            productie bijkleuren. Druk op een titel en laat het de ruimte geven
            die je nodig acht.
          </motion.p>
        </div>

        <div>
          {tracks.length === 0 ? (
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
              data-testid="tracks-empty-state"
              className="font-serif italic text-2xl font-light text-sand-900/50 border-l-2 border-rose-300 pl-6 py-4"
            >
              Binnenkort klinkt hier de eerste opname.
            </motion.p>
          ) : (
            <ul className="divide-y divide-sand-200">
              {tracks.map((track, i) => {
                const active = currentTrack?.id === track.id;
                return (
                  <motion.li
                    key={track.id}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.7, delay: i * 0.08 }}
                  >
                    <button
                      type="button"
                      onClick={() => playTrack(track)}
                      data-testid={`track-play-button-${i}`}
                      className="group w-full flex items-center gap-6 py-7 text-left"
                    >
                      <span className="font-serif text-sm text-sand-900/40 w-8 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className={`block font-serif text-2xl sm:text-3xl font-light tracking-tight transition-colors duration-300 ${active ? "text-terracotta-500" : "text-sand-900 group-hover:text-terracotta-500"}`}>
                          {track.title}
                        </span>
                        {track.description && (
                          <span className="block mt-1 font-sans text-sm font-light text-sand-900/50 truncate">
                            {track.description}
                          </span>
                        )}
                      </span>
                      <span className={`w-12 h-12 shrink-0 rounded-full border flex items-center justify-center transition-colors duration-300 ${active ? "border-terracotta-500 bg-terracotta-500 text-sand-50" : "border-sand-300 text-sand-900 group-hover:border-terracotta-500 group-hover:text-terracotta-500"}`}>
                        {active && isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
                      </span>
                    </button>
                  </motion.li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};
